import { useState } from "react";
import { Modal, Input, Select, Button, Alert } from "@marekl/adminer/components";
import { useImportBarcodes } from "../../../lib/queries/barcodeBank";
import type { ImportRange, ImportResult } from "../../../lib/queries/barcodeBank";
import { useVoucherTypes } from "../../../lib/queries/voucherTypes";
import { notifyError } from "@marekl/adminer/api";
import styles from "./ImportModal.module.css";

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
}

interface RangeRow {
  id: number;
  from: string;
  to: string;
  voucher_type_id: string;
}

let nextId = 1;

function emptyRow(): RangeRow {
  return { id: nextId++, from: "", to: "", voucher_type_id: "" };
}

export function ImportModal({ open, onClose }: ImportModalProps) {
  const importBarcodes = useImportBarcodes();
  const { data: voucherTypes = [] } = useVoucherTypes();
  const [rows, setRows] = useState<RangeRow[]>([emptyRow()]);
  const [result, setResult] = useState<ImportResult | null>(null);

  function handleClose() {
    setRows([emptyRow()]);
    setResult(null);
    onClose();
  }

  function updateRow(id: number, field: keyof RangeRow, value: string) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(id: number) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }

  async function handleImport() {
    const ranges: ImportRange[] = rows
      .filter((r) => r.from && r.to && r.voucher_type_id)
      .map((r) => ({
        from: r.from.trim(),
        to: r.to.trim(),
        voucher_type_id: Number(r.voucher_type_id),
      }));
    if (ranges.length === 0) return;
    try {
      const res = await importBarcodes.mutateAsync(ranges);
      setResult(res);
    } catch (err) {
      notifyError(err);
    }
  }

  const canSubmit = rows.some((r) => r.from && r.to && r.voucher_type_id);

  return (
    <Modal
      open={open}
      title="Import dávky kódů"
      onClose={handleClose}
      width={680}
      footer={
        result ? (
          <Button variant="primary" onClick={handleClose}>
            Zavřít
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Zrušit</Button>
            <Button
              variant="primary"
              loading={importBarcodes.isPending}
              onClick={handleImport}
              disabled={!canSubmit}
            >
              Importovat
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.result}>
          <Alert variant="ok">
            Importováno {result.inserted} nových kódů
            {result.skipped > 0 && `, přeskočeno ${result.skipped} (duplicity)`}.
          </Alert>
          {(result.rejected?.length ?? 0) > 0 && (
            <Alert variant="warn">
              <strong>Odmítnuto {result.rejected!.length} kódů:</strong>
              <ul className={styles.rejectedList}>
                {result.rejected!.slice(0, 10).map((r) => (
                  <li key={r.barcode}>
                    <code>{r.barcode}</code> — {r.reason}
                  </li>
                ))}
                {result.rejected!.length > 10 && (
                  <li>… a {result.rejected!.length - 10} dalších</li>
                )}
              </ul>
            </Alert>
          )}
        </div>
      ) : (
        <div className={styles.form}>
          <p className={styles.hint}>
            Zadejte rozsahy EAN-13 kódů (od–do). Kódy musí mít platný kontrolní součet a nesmí začínat 029.
          </p>
          <div className={styles.rangeList}>
            {rows.map((row) => (
              <div key={row.id} className={styles.rangeRow}>
                <Input
                  id={`rng-from-${row.id}`}
                  label="Od"
                  placeholder="8594000124001"
                  value={row.from}
                  onChange={(e) => updateRow(row.id, "from", e.target.value)}
                  className={styles.mono}
                />
                <Input
                  id={`rng-to-${row.id}`}
                  label="Do"
                  placeholder="8594000124100"
                  value={row.to}
                  onChange={(e) => updateRow(row.id, "to", e.target.value)}
                  className={styles.mono}
                />
                <Select
                    label="Typ"
                    id={`rng-type-${row.id}`}
                    value={row.voucher_type_id}
                    onChange={(e) => updateRow(row.id, "voucher_type_id", e.target.value)}
                  >
                    <option value="">— typ —</option>
                    {voucherTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.name}
                      </option>
                    ))}
                </Select>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeRow(row.id)}
                  aria-label="Odebrat řádek"
                  disabled={rows.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <Button onClick={addRow}>+ Přidat rozsah</Button>
        </div>
      )}
    </Modal>
  );
}
